import { FC, MouseEventHandler, useRef } from 'react';
import cx from 'classnames';
import Checkmark from 'components/Icons/Checkmark';
import { changeSunglasses } from 'functions/changeSunglasses';

interface MultiselectFilterProps {
  options: string[];
  value: string[];
  // eslint-disable-next-line no-unused-vars
  onUpdateValue: (value: string[]) => void;
}

interface MultiselectFilterOptionProps {
  selected: boolean;
  // eslint-disable-next-line no-unused-vars
  toggleSelection: MouseEventHandler<HTMLDivElement>;
}

const MultiselectFilterOption: FC<MultiselectFilterOptionProps> = ({
  selected,
  toggleSelection,
  children
}) => {
  return (
    <div
      className={cx(
        'mt-[8px] mr-[8px] flex h-[40px] cursor-pointer items-center rounded-[8px] border px-[14px] transition-all last:mr-0',
        {
          'select-none border-brand-grey400 bg-white text-brand-black100 hover:text-brand-orange':
            !selected,
          'border-brand-orange bg-brand-orange text-white': selected
        }
      )}
      data-selected={selected}
      onClick={toggleSelection}>
      {!!selected && <Checkmark fill="white" className="mr-[6px]" />}
      <span className="mb-[3px] text-[13px] font-medium">{children}</span>
    </div>
  );
};

const MultiselectFilter: FC<MultiselectFilterProps> = ({
  options,
  value,
  onUpdateValue
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  const scrollToOption = (optionIndex: number) => {
    if (!wrapperRef.current) return;

    const targetOptionElement = wrapperRef.current?.children[optionIndex];

    if (!targetOptionElement) return;

    setTimeout(() => {
      targetOptionElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start'
      });
    }, 150);
  };

  const toggleSelection = (index: number, option: string) => {
    const isSelected = value.includes(option);

    if (!isSelected) {
      scrollToOption(index);
      onUpdateValue([...value, option]);
      return;
    }

    return onUpdateValue(
      value.filter((selectedOption) => selectedOption !== option)
    );
  };

  return (
    <div
      className="mt-[-8px] mb-[12px] flex overflow-x-auto whitespace-nowrap pb-[24px] pt-[18px] lg:mb-0 lg:flex-wrap"
      ref={wrapperRef}>
      {options.map((option, index) => (
        <MultiselectFilterOption
          key={option}
          toggleSelection={() => toggleSelection(index, option)}
          selected={value.includes(option)}>
          {changeSunglasses(option, { capitalize: true })}
        </MultiselectFilterOption>
      ))}
    </div>
  );
};

export default MultiselectFilter;
